import React, { useState, useEffect, useCallback } from 'react';
import { X, Play, Loader2, Copy, Check, Link as LinkIcon, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';
import NotificationPopup from '../admin/component/NotificationPopup';
import PremiumSelect from '../admin/component/PremiumSelect';
const getLowCreditThreshold = () => {
    const envVal = process.env.REACT_APP_LOW_CREDIT_THRESHOLD;
    const parsed = parseInt(envVal, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const isCreditLow = (balance) => {
    if (balance === undefined || balance === null) return false;
    return Number(balance) <= getLowCreditThreshold();
};

const mapExperienceLevel = (level) => {
    if (!level) return '';
    const clean = level.toLowerCase().replace('_level', '').replace('-', ' ').trim();
    if (clean.includes('entry') || clean.includes('fresh')) return 'entry';
    if (clean.includes('junior')) return 'junior';
    if (clean.includes('mid')) return 'mid';
    if (clean.includes('senior')) return 'senior';
    if (clean.includes('lead') || clean.includes('manager')) return 'lead';
    if (clean.includes('executive') || clean.includes('director') || clean.includes('vp') || clean.includes('cxo')) return 'executive';

    const valid = ['entry', 'junior', 'mid', 'senior', 'lead', 'executive'];
    return valid.includes(clean) ? clean : '';
};

const B2CInterviewSetupModal = ({
    isOpen,
    onClose,
    resume,
    onStartInterview,
    creditBalance,
    initialSkills = null,
    initialExperienceLevel = null,
    jobDescription = null,
    targetRole = null,
    companyName = null,
}) => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [extractingExperience, setExtractingExperience] = useState(false);
    const [experienceLevel, setExperienceLevel] = useState('');
    const [extractedSkills, setExtractedSkills] = useState([]);
    const [additionalSkillsInput, setAdditionalSkillsInput] = useState('');
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [error, setError] = useState('');
    const [existingSessionId, setExistingSessionId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [copySuccess, setCopySuccess] = useState(false);
    const [sessionData, setSessionData] = useState(null); // State to hold generated session data
    const [localCreditBalance, setLocalCreditBalance] = useState(null);

    // Experience level options from models.py
    const experienceLevelOptions = [
        { value: 'entry', label: t('publicJobDescriptions.entryLevel', 'Entry Level (0-1 years)') },
        { value: 'junior', label: t('publicJobDescriptions.juniorLevel', 'Junior (1-3 years)') },
        { value: 'mid', label: t('publicJobDescriptions.midLevel', 'Mid Level (3-5 years)') },
        { value: 'senior', label: t('publicJobDescriptions.seniorLevel', 'Senior (5-8 years)') },
        { value: 'lead', label: t('publicJobDescriptions.leadLevel', 'Lead (8+ years)') },
        { value: 'executive', label: t('publicJobDescriptions.executive', 'Executive (10+ years)') }
    ];

    // Extract experience level from resume
    const extractExperienceLevel = useCallback(async () => {
        if (!resume) return;

        setExtractingExperience(true);

        try {
            // Call the OpenAI-based experience extraction API
            const response = await api.post('resumes/extract-experience/', {
                resume_id: resume.id
            });

            if (response.data.experience_level) {
                console.log('Extracted experience:', response.data);
                setExperienceLevel(response.data.experience_level);
            } else {
                // Extraction failed or returned null - reset to empty
                setExperienceLevel('');
            }
        } catch (err) {
            console.error('Error extracting experience level:', err);
            // Extraction failed - reset to empty so user can select manually
            setExperienceLevel('');
        } finally {
            setExtractingExperience(false);
        }
    }, [resume]);

    // Initialize experience level from resume parsing
    useEffect(() => {
        // Reset experience level when resume changes
        setExperienceLevel('');
        setExtractingExperience(false);
        setError(''); // Clear any previous errors
        setExistingSessionId(null);
        setSessionData(null); // Reset session data on new resume

        if (initialExperienceLevel) {
            setExperienceLevel(mapExperienceLevel(initialExperienceLevel));
        } else if (resume?.parsed_data?.experience_level) {
            setExperienceLevel(resume.parsed_data.experience_level);
        } else {
            // Try to extract experience level if not available
            if (resume?.parsed_data?.raw_text) {
                extractExperienceLevel();
            }
        }
    }, [resume, extractExperienceLevel, initialExperienceLevel]);

    // Initialize extracted skills from resume
    useEffect(() => {
        // Reset skills when resume changes
        setSelectedSkills([]);
        setAdditionalSkillsInput('');

        if (initialSkills && initialSkills.length > 0) {
            setExtractedSkills(initialSkills);
            setSelectedSkills(initialSkills);
        } else if (resume?.parsed_data?.skills) {
            setExtractedSkills(resume.parsed_data.skills);
            // Don't auto-populate selected skills - start empty
        } else {
            setExtractedSkills([]);
        }
    }, [resume, initialSkills]);

    // This effect runs when the modal opens to check for a stored, valid token.
    useEffect(() => {
        const validateStoredToken = async () => {
            if (!resume?.id) return;

            const storageKey = `practice_interview_session_${resume.id}`;
            const storedSessionJSON = localStorage.getItem(storageKey);

            if (storedSessionJSON) {
                const storedSession = JSON.parse(storedSessionJSON);
                if (storedSession?.token) {
                    setLoading(true);
                    try {
                        // Validate the token using the provided API endpoint
                        await api.get(`/interview/validate/${storedSession.token}/`);
                        // If validation succeeds, set the session data to show Copy/Start buttons
                        console.log("Found and validated existing token.");
                        setSessionData(storedSession);
                    } catch (err) {
                        // This likely means the token is expired or invalid
                        console.log('Stored token is invalid, removing it.', err);
                        localStorage.removeItem(storageKey);
                        setSessionData(null);
                    } finally {
                        setLoading(false);
                    }
                }
            } else {
                // No stored session, ensure UI is in the 'generate' state.
                setSessionData(null);
            }
        };

        if (isOpen) {
            validateStoredToken();
        }
    }, [isOpen, resume]);

    useEffect(() => {
        const fetchLocalCredits = async () => {
            if (creditBalance !== undefined && creditBalance !== null) {
                setLocalCreditBalance(creditBalance);
                return;
            }
            try {
                const response = await api.get('observability/user-credits/');
                const data = response.data.results || response.data;
                const balance = data.current_balance ?? data.currentBalance ?? data.remaining_credits ?? data.remainingCredits ?? 0;
                setLocalCreditBalance(Number(balance));
            } catch (err) {
                console.error("Failed to fetch credits in modal:", err);
            }
        };

        if (isOpen) {
            fetchLocalCredits();
        }
    }, [isOpen, creditBalance]);




    const handleSkillSelection = (skill) => {
        if (selectedSkills.includes(skill)) {
            // Remove skill if already selected
            setSelectedSkills(prev => prev.filter(s => s !== skill));
        } else {
            // Add skill if not selected
            setSelectedSkills(prev => [...prev, skill]);
        }
    };

    const handleAddSkill = (skill) => {
        if (skill.trim() && !selectedSkills.includes(skill.trim())) {
            setSelectedSkills(prev => [...prev, skill.trim()]);
            setAdditionalSkillsInput('');
        }
    };

    const handleRemoveSkill = (skillToRemove) => {
        setSelectedSkills(prev => prev.filter(skill => skill !== skillToRemove));
    };

    const handleGenerateLink = async () => {
        if (loading) {
            console.log('Link generation already in progress, ignoring duplicate call');
            return;
        }

        if (!experienceLevel) {
            setError(t('b2cDashboard.setup.errorSelectExperience', 'Please select an experience level.'));
            return;
        }

        if (selectedSkills.length === 0) {
            setError(t('b2cDashboard.setup.errorAddSkill', 'Please add at least one skill.'));
            return;
        }

        setLoading(true);
        setError('');
        setExistingSessionId(null); // Reset on new attempt

        try {

            const payload = {
                resume_id: resume.id,
                practice_session: true,
                selected_skills: selectedSkills,
                experience_level: experienceLevel,
                language: (i18n.language || 'en').split('-')[0]
            };
            if (jobDescription) {
                payload.job_description = jobDescription;
            }
            if (targetRole) {
                payload.target_role = targetRole;
            }
            if (companyName) {
                payload.company_name = companyName;
            }

            const response = await api.post('job-applications/{application_pk}/create_interview_session/', payload);
            const newSessionData = {
                // id: response.data.id,
                token: response.data.token,
            };
            // Store the session data in state and localStorage
            setSessionData(newSessionData);
            localStorage.setItem(`practice_interview_session_${resume.id}`, JSON.stringify(newSessionData));
            setLocalCreditBalance(prev => (prev !== null ? Math.max(0, prev - 1) : prev));

            setSnackbar({ open: true, message: t('b2cDashboard.setup.linkGeneratedSuccess', 'Interview link generated successfully!'), severity: 'success' });
        } catch (err) {
            console.error('Error generating interview link:', err);
            const errorMessage = err.response?.data?.error || 'Failed to generate link. Please try again.';

            if (errorMessage.includes('Insufficient credits')) {
                const creditsError = t('b2cDashboard.setup.insufficientCreditsMessage', 'Insufficient credits. You need at least 1 credit to start a practice interview. Available: {{count}} credits.', { count: err.response?.data?.credits_available || 0 });
                setError(creditsError);
                setSnackbar({ open: true, message: creditsError, severity: 'error' });
            } else if (errorMessage.includes('already have a practice interview session')) {
                setError(errorMessage);
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                if (err.response.data.existing_session_id) {
                    setExistingSessionId(err.response.data.existing_session_id);
                }
            } else {
                setError(errorMessage);
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStartInterview = () => {
        if (!sessionData) {
            console.error('No session data available to start interview.');
            setError(t('b2cDashboard.setup.errorGenerateLinkFirst', 'Please generate an interview link first.'));
            return;
        };
        onClose();
        onStartInterview(sessionData.id, sessionData.token);
    };

    const handleCancelAndRetry = async () => {
        if (!existingSessionId || loading) return;

        setLoading(true);
        setError('');

        try {
            //  The API endpoint to expire is POST /sessions/{id}/expire/
            await api.post(`/api/interviews/admin/expire-invite/<token>/`);

            setExistingSessionId(null);
            setError(''); // Clear the error to allow retrying link generation

            // Also remove the now-expired token from storage
            if (resume?.id) {
                localStorage.removeItem(`practice_interview_session_${resume.id}`);
            }

            console.log('Successfully expired previous session. You can now generate a new link.');
            setSnackbar({ open: true, message: t('b2cDashboard.setup.prevExpiredSuccess', 'Previous interview expired successfully!'), severity: 'success' });
        } catch (cancelErr) {
            console.error('Failed to expire interview:', cancelErr);
            setError(t('b2cDashboard.setup.failedExpire', 'Failed to expire the previous interview. You may need to do it from your dashboard.'));
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSkill(additionalSkillsInput);
        }
    };

    const handleCopyLink = async () => {
        if (!sessionData) {
            setError(t('b2cDashboard.setup.errorGenerateLinkFirst', 'Please generate the interview link first.'));
            return;
        }

        setLoading(true);
        setError('');
        setCopySuccess(false);

        try {
            // Construct the interview link using the stored session data
            let baseInterviewUrl;

            if (process.env.REACT_APP_INTERVIEW_UI_URL) {
                // Use environment variable if set (Dev/Prod)
                baseInterviewUrl = process.env.REACT_APP_INTERVIEW_UI_URL;
            } else {
                // Use current origin for local development
                baseInterviewUrl = window.location.origin;
            }

            // Per user instruction, we use the data from the session creation API.
            // That API returns a `token`, which we assume is the invite token for the URL.
            const relativePath = `/interview/invite/${sessionData.token}`;
            const interviewLink = `${baseInterviewUrl}${relativePath}`;

            // Copy to clipboard
            await navigator.clipboard.writeText(interviewLink);

            setCopySuccess(true);
            setSnackbar({ open: true, message: t('b2cDashboard.setup.linkCopiedClipboard', 'Interview link copied to clipboard!'), severity: 'success' });

            // Reset copy success after 3 seconds
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (err) {
            console.error('Error copying interview link:', err);
            setError(t('b2cDashboard.setup.failedCopyLink', 'Failed to copy interview link.'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-md flex items-center justify-center p-4">
            <NotificationPopup open={snackbar.open} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} message={snackbar.message} />
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-5 bg-gradient-to-r from-[#0ea5e9] to-[#d946ef] text-white">
                    <div>
                        <h2 className="text-lg font-bold text-white">{t('b2cDashboard.setup.title', 'Interview Setup')}</h2>
                        <p className="text-[10px] text-white uppercase tracking-wider font-bold">
                            {resume?.name ? `${resume.name} — ${t('b2cDashboard.setup.practiceSession', 'Practice Session')}` : t('b2cDashboard.setup.practiceSession', 'Practice Session')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 text-white rounded-xl transition-all duration-300"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Skills and Experience Info */}
                    <div className="text-center bg-gradient-to-br from-blue-50 to-indigo-50/30 border border-blue-100 rounded-xl py-3 px-4 shadow-sm w-full">
                        <h3 className="text-blue-900 font-bold mb-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            {t('b2cDashboard.setup.skillsExtracted', '🚀 Skills & Experience Automatically Extracted')}
                        </h3>
                        <p className="text-[11px] sm:text-xs text-blue-700 font-medium leading-relaxed">
                            {t('b2cDashboard.setup.extractedDescription', 'Your resume has been analyzed by Miro to extract skills and experience level. No need to click any buttons - just select your preferred skills below!')}
                        </p>
                    </div>

                    {/* Experience Level */}
                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                            {t('b2cDashboard.experienceLevel', 'Experience Level')}
                        </label>
                        <PremiumSelect
                            variant="b2c"
                            value={experienceLevel}
                            onChange={setExperienceLevel}
                            disabled={extractingExperience}
                            placeholder={extractingExperience ? t('b2cDashboard.setup.extracting', 'Extracting from resume...') : t('b2cDashboard.setup.selectExperience', 'Select experience level')}
                            options={experienceLevelOptions}
                        />
                        <p className="text-xs text-gray-500 font-medium flex items-center gap-1 mt-1.5">
                            {t('b2cDashboard.setup.autoExtractedExperience', '✅ Experience level automatically extracted from your resume')}
                        </p>
                    </div>

                    {/* Extracted Skills Card */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        <div className="px-5 py-3 bg-blue-50 border-b border-blue-200 flex items-center gap-3">
                            <div className="bg-blue-100 p-2 rounded-full text-blue-600">
                                <User size={18} />
                            </div>
                            <div>
                                <h4 className="text-blue-800 font-semibold text-base">
                                    {t('b2cDashboard.setup.extractedSkillsLabelCard', 'Extracted Skills')}
                                </h4>
                                <p className="text-blue-700 text-xs">
                                    {t('b2cDashboard.setup.extractedSkillsSubtitle', 'Skills found in resume (click to select)')}
                                </p>
                            </div>
                        </div>

                        <div className="p-4 flex flex-wrap gap-2">
                            {extractedSkills.length === 0 ? (
                                <span className="text-gray-400 text-sm italic">
                                    {t('b2cDashboard.setup.noExtractedSkills', 'No skills extracted from resume')}
                                </span>
                            ) : (
                                extractedSkills.map((skill, index) => {
                                    const isSelected = selectedSkills.includes(skill);
                                    return (
                                        <span
                                            key={index}
                                            onClick={() => handleSkillSelection(skill)}
                                            className={`px-2 py-1 text-sm rounded-md cursor-pointer border select-none transition-all duration-200 ${isSelected
                                                ? 'text-white border-blue-600 shadow-sm'
                                                : 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                                                }`}
                                            style={isSelected ? { background: 'linear-gradient(135deg, #3b82f6, #2563eb)' } : {}}
                                        >
                                            {skill}
                                        </span>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Additional Skills */}
                    <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700">
                            {t('b2cDashboard.setup.additionalSkills', 'Additional Skills')}
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={additionalSkillsInput}
                                onChange={(e) => setAdditionalSkillsInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={t('b2cDashboard.setup.typeSkillPlaceholder', 'Type a skill and press Enter')}
                                className="w-full pl-3 pr-20 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-700 font-medium hover:border-blue-300 hover:shadow-sm"
                            />
                            <button
                                onClick={() => handleAddSkill(additionalSkillsInput)}
                                disabled={!additionalSkillsInput.trim()}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                            >
                                {t('b2cDashboard.setup.add', 'Add')}
                            </button>
                        </div>
                    </div>

                    {/* Selected Skills */}
                    <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700">
                            {t('b2cDashboard.setup.selectedSkillsLabel', 'Selected Skills')}
                        </label>
                        <div className="flex flex-wrap gap-2 min-h-[60px] max-h-[120px] overflow-y-auto p-3 border border-gray-200 rounded-xl bg-gray-50 shadow-inner scrollbar-thin">
                            {selectedSkills.length === 0 ? (
                                <span className="text-gray-400 text-sm italic">{t('b2cDashboard.setup.noSkillsSelected', 'No skills selected')}</span>
                            ) : (
                                selectedSkills.map((skill, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold shadow-sm"
                                    >
                                        {skill}
                                        <button
                                            onClick={() => handleRemoveSkill(skill)}
                                            className="ml-2 text-blue-500 hover:text-blue-700 transition-colors"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Low Credit Warning */}
                    {isCreditLow(localCreditBalance) && !error && localCreditBalance >= 0 && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex flex-col items-start mb-4">
                            <span className="font-semibold text-sm">
                                {localCreditBalance === 0
                                    ? t('b2cDashboard.setup.zeroCreditWarning', '⚠️ Insufficient Credits: You have 0 credits remaining. Please purchase more credits to continue.')
                                    : t('b2cDashboard.setup.lowCreditWarning', '⚠️ Low Credit Warning: You only have {{count}} credit(s) remaining.', { count: localCreditBalance })
                                }
                            </span>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex flex-col items-start">
                            <span>{error}</span>
                            {existingSessionId && (
                                <button
                                    onClick={handleCancelAndRetry}
                                    disabled={loading}
                                    className="mt-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                >
                                    {loading ? t('b2cDashboard.setup.expiring', 'Expiring...') : t('b2cDashboard.setup.expirePrevious', 'Expire Previous Session')}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                    {!sessionData ? (
                        <button
                            onClick={handleGenerateLink}
                            disabled={loading || !experienceLevel || selectedSkills.length === 0}
                            className="bg-gradient-to-r from-[#0ea5e9] to-[#d946ef] hover:opacity-90 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-2.5 rounded-xl transition-all duration-300 flex items-center space-x-2 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform active:scale-95 font-semibold"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <LinkIcon className="w-4 h-4" />
                            )}
                            <span>{loading ? t('b2cDashboard.setup.generatingLink', 'Generating Link...') : t('b2cDashboard.setup.generateLink', 'Generate Interview Link')}</span>
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={handleCopyLink}
                                disabled={loading}
                                className="bg-gradient-to-r from-[#0ea5e9] to-[#d946ef] hover:opacity-90 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-2.5 rounded-xl transition-all duration-300 flex items-center space-x-2 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform active:scale-95 font-semibold"
                            >
                                {copySuccess ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                                <span>{copySuccess ? t('b2cDashboard.setup.linkCopied', 'Link Copied!') : t('b2cDashboard.setup.copyLink', 'Copy Link')}</span>
                            </button>
                            <button
                                onClick={handleStartInterview}
                                disabled={loading}
                                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-2.5 rounded-xl transition-all duration-300 flex items-center space-x-2 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform active:scale-95 font-semibold"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Play className="w-4 h-4" />
                                )}
                                <span>{loading ? t('b2cDashboard.starting', 'Starting...') : t('b2cDashboard.startInterview', 'Start Interview')}</span>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default B2CInterviewSetupModal;
